import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Dartboard from "../components/input/Dartboard";
import Controls from "../components/input/Controls";
import CurrentRoundStats from "../components/input/CurrentStats";
import WarmupTimer from "../components/input/WarmupTimer";
import { undoHit } from "../store/slices/dartSlice";
import { motion } from "framer-motion";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { FaHourglass } from "react-icons/fa";
import { FaBug } from "react-icons/fa";
import "../styles/App.css";

const App = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const state = useSelector((state) => state.dartGame);
  const debugMode = useSelector((state) => state.user.debugMode);
  const [isZoomed, setIsZoomed] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [showWarmupTimer, setShowWarmupTimer] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (showHelp) {
        setShowHelp(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showHelp]);

  const toggleZoom = () => {
    setIsZoomed((prevZoom) => !prevZoom);
  };

  const handleUndo = () => dispatch(undoHit());

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-2  md:py-6  sm:px-6 lg:px-8 bg-gradient from-primary to-secondary"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}

        {/* Main Content */}
        <div className="grid gap-2 md:gap-8 md:grid-cols-[2fr,1fr]">
          {/* Left Column - Dartboard */}
          <motion.div
            layout
            className="bg-primary/50 md:p-4  md:p2 rounded-2xl shadow-xl border border-dark"
          >
            <Dartboard isZoomed={isZoomed} />
          </motion.div>

          {/* Right Column - Stats and Controls */}
          <div className="space-y-4">
            <motion.div
              layout
              className="bg-primary/50 md:p-4  p-2 rounded-2xl shadow-xl border border-dark"
            >
              <CurrentRoundStats
                roundHistory={state.roundHistory}
                currentRound={state.currentRound}
              />
            </motion.div>

            <motion.div
              layout
              className="bg-primary/50 p-4 rounded-2xl shadow-xl border border-dark"
            >
              <Controls
                toggleZoom={toggleZoom}
                undoHit={handleUndo}
                isZoomed={isZoomed}
              />
            </motion.div>

            {/* Session Summary */}
            <motion.div
              layout
              className="bg-primary/50 p-4 rounded-2xl shadow-xl border border-dark"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-gray-400 text-sm">
                    {t("input.maximumScore")}
                  </p>
                  <p className="text-2xl font-bold text-white">
                    {state.roundHistory.length > 0
                      ? Math.max(
                          ...state.roundHistory.map((round) => round.totalScore)
                        )
                      : 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-sm">{t("input.rounds")}</p>
                  <p className="text-2xl font-bold text-white">
                    {state.roundHistory.length}
                  </p>
                </div>
              </div>
            </motion.div>

            <div className="flex justify-center">
              <button
                onClick={() => setShowWarmupTimer(true)}
                className="flex items-center px-4 py-2 bg-primary/50 hover:bg-secondary/80 text-lightGray rounded-lg transition-colors text-lightGray"
              >
                <FaHourglass className="mr-2 text-lightGray" />
                {t("input.warmup")}
              </button>
            </div>
          </div>

          {/* Warmup Timer */}
          {showWarmupTimer && (
            <WarmupTimer onClose={() => setShowWarmupTimer(false)} />
          )}

          {/* Debug Panel - Only visible when debug mode is enabled */}
          {debugMode && (
            <div className="fixed bottom-4 right-4 bg-primary/90 p-4 rounded-xl shadow-xl border border-dark max-w-xs max-h-[30vh] overflow-auto text-sm">
              <h3 className="font-semibold text-white mb-2 text-center">
                Debug Info
              </h3>
              <div className="space-y-2">
                <div>
                  <h4 className="text-gray-400">Current Round:</h4>
                  <div className="bg-gray-800/50 p-2 rounded">
                    {Array.isArray(state.currentRound) &&
                      state.currentRound.map((hit, idx) => (
                        <div key={idx} className="text-lightGray">
                          {idx + 1}: {hit.label}
                        </div>
                      ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-gray-400">Round History:</h4>
                  <div className="bg-gray-800/50 p-2 rounded">
                    {Array.isArray(state.roundHistory) &&
                      state.roundHistory.map((round, roundIdx) => (
                        <div key={roundIdx} className="text-lightGray">
                          Round {state.roundHistory.length - roundIdx}:{" "}
                          {Array.isArray(round.hits)
                            ? round.hits.map((hit) => hit.label).join(", ")
                            : "No hits"}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Help Button */}
          <div className="flex justify-center mb-6 md:mt-2 mt-4">
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <IoMdInformationCircleOutline className="w-6 h-6 mr-2" />
              <span>{t("input.howToUse")}</span>
            </button>
          </div>

          {/* Help Panel */}
          {showHelp && (
            <div
              className="fixed inset-0 flex items-center justify-center z-50"
              onClick={() => setShowHelp(false)}
            >
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 bg-primary rounded-xl shadow-xl border border-gray-700 text-sm text-gray-300 w-[90%] max-w-sm md:max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-semibold text-white mb-4 text-center text-xl">
                  {t("input.quickGuide")}:
                </h3>
                <ul className="space-y-3">
                  <li>• {t("input.help.clickDartboard")}</li>
                  <li>• {t("input.help.useZoom")}</li>
                  <li>• {t("input.help.trackStats")}</li>
                  <li>• {t("input.help.useUndo")}</li>
                  <li>• {t("input.help.completeRound")}</li>
                </ul>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default App;
